import type { ToRefs } from 'vue';
import { toRefs, useHost, watch, onBeforeUnmount } from 'vue';
import { useComponentContext } from 'composables/useComponentContext';
import { createComponentState } from 'types/componentState';
import { useStateStore } from 'composables/useStateStore';
import { unwrap } from 'types/utils';

/**
 * Creates a reactive shared state for component props that syncs with the global state store.
 *
 * This composable:
 * - Registers the component in the global state store
 * - Establishes two-way binding between props and store state
 * - Listens to `update:propName` events from the custom element host
 * - Watches for external prop changes from the parent component
 * - Automatically cleans up listeners and watchers on unmount
 *
 * **Performance notes:**
 * - Uses shallow watchers (not deep) to avoid expensive traversal
 * - Only triggers updates on object identity changes, not internal mutations
 * - Cleanup is automatic and comprehensive
 *
 * @template T - The component props type (must be an object)
 * @param props - The readonly props object from the component's setup function
 * @returns Reactive refs to the props stored in the shared state
 * @throws {Error} If called outside a component instance (context.uid is null)
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { useSharedState } from '@/runtime/composables/useSharedState';
 *
 * interface Props {
 *   value: string;
 *   count: number;
 * }
 *
 * const props = defineProps<Props>();
 *
 * // Get reactive refs synced with global state
 * const state = useSharedState<Props>(props);
 *
 * // Access props through state refs
 * console.log(state.value.value); // 'hello'
 * console.log(state.count.value); // 42
 * </script>
 *
 * <template>
 *   <div>
 *     <p>Value: {{ state.value }}</p>
 *     <p>Count: {{ state.count }}</p>
 *   </div>
 * </template>
 * ```
 */
export const useSharedState = <T extends object>(props: Readonly<T>): ToRefs<T> => {
  const stateStore = useStateStore<T>();
  const context = useComponentContext();
  const host = useHost();

  if (context.uid == null) {
    throw new Error('useSharedState must be used within a component instance.');
  }

  const key = context.uid.toString();
  const parentKey = context.parentUid;
  const type = context.typeName ?? 'unknown';

  // initialize once
  if (!stateStore.state[key]) {
    stateStore.setState(key, createComponentState<T>(context.uid, type, parentKey, unwrap(props)));
  }

  // Cleanup tracker for all watchers and listeners
  const cleanup: Array<() => void> = [];

  // reflect CE v-model/update events back into the store
  if (host) {
    setupHostEventListeners(host, props, stateStore, key, cleanup);
  }

  setupPropWatchers(props, stateStore, key, cleanup);

  onBeforeUnmount(() => {
    cleanup.forEach(fn => fn());
    cleanup.length = 0;
  });

  // return refs to the live object kept in the store
  return toRefs(stateStore.state[key].props);
};

/**
 * Sets up event listeners on the custom element host to sync v-model updates back to the store.
 *
 * For each prop, listens to `update:propName` events emitted by the custom element
 * and updates the corresponding store value.
 *
 * @param host - The custom element host
 * @param props - The component props
 * @param stateStore - The state store instance
 * @param key - The component's unique identifier
 * @param cleanup - Array to track cleanup functions
 */
const setupHostEventListeners = <T extends object>(
  host: HTMLElement,
  props: Readonly<T>,
  stateStore: ReturnType<typeof useStateStore<T>>,
  key: string,
  cleanup: Array<() => void>
): void => {
  for (const propName in unwrap(props)) {
    const evt = `update:${propName}`;
    const handler = (e: Event): void => {
      if (!(e instanceof CustomEvent)) return;
      const next = Array.isArray(e.detail) ? e.detail[0] : e.detail;
      stateStore.setPropValue(key, propName as keyof T, next as T[keyof T]);
    };
    host.addEventListener(evt, handler);
    cleanup.push(() => host.removeEventListener(evt, handler));
  }
};

/**
 * Sets up shallow watchers for each prop to detect external changes from the parent.
 *
 * Uses `{ deep: false }` to only watch object identity changes, avoiding expensive
 * deep traversal and JSON serialization overhead.
 *
 * @param props - The component props
 * @param stateStore - The state store instance
 * @param key - The component's unique identifier
 * @param cleanup - Array to track cleanup functions
 */
const setupPropWatchers = <T extends object>(
  props: Readonly<T>,
  stateStore: ReturnType<typeof useStateStore<T>>,
  key: string,
  cleanup: Array<() => void>
): void => {
  for (const propName in unwrap(props)) {
    const stopWatch = watch(
      () => (props as any)[propName],
      (next, prev) => {
        // Only update if value actually changed (identity check for objects, equality for primitives)
        if (next !== prev) {
          stateStore.setPropValue(key, propName as keyof T, next as T[keyof T]);
        }
      },
      { deep: false }
    );
    cleanup.push(stopWatch);
  }
};
